import { useEffect, useState } from 'react'
import {
	BuildingOffice2Icon,
	CheckIcon,
	DevicePhoneMobileIcon,
	MapPinIcon,
	UserCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

export default function DialerDetailsCard({
	dialerDetails,
	onFacilityAddressUpdate,
	onFacilityNameUpdate,
	onRepresentativeNameUpdate,
	onRepresentativePhoneUpdate,
}) {
	const { t } = useTranslation()
	const [isEditingFacilityName, setIsEditingFacilityName] = useState(false)
	const [isEditingAddress, setIsEditingAddress] = useState(false)
	const [isEditingRepresentativeName, setIsEditingRepresentativeName] = useState(false)
	const [isEditingRepresentativePhone, setIsEditingRepresentativePhone] = useState(false)
	const [facilityName, setFacilityName] = useState(dialerDetails?.placowkaName || '')
	const [facilityAddress, setFacilityAddress] = useState(dialerDetails?.placowkaAddress || '')
	const [representativeName, setRepresentativeName] = useState(dialerDetails?.representativeName || '')
	const [representativePhone, setRepresentativePhone] = useState(dialerDetails?.representativePhone || '')

	useEffect(() => {
		if (!isEditingFacilityName) {
			setFacilityName(dialerDetails?.placowkaName || '')
		}
	}, [dialerDetails?.placowkaName, isEditingFacilityName])

	useEffect(() => {
		if (!isEditingAddress) {
			setFacilityAddress(dialerDetails?.placowkaAddress || '')
		}
	}, [dialerDetails?.placowkaAddress, isEditingAddress])

	useEffect(() => {
		if (!isEditingRepresentativeName) {
			setRepresentativeName(dialerDetails?.representativeName || '')
		}
	}, [dialerDetails?.representativeName, isEditingRepresentativeName])

	useEffect(() => {
		if (!isEditingRepresentativePhone) {
			setRepresentativePhone(dialerDetails?.representativePhone || '')
		}
	}, [dialerDetails?.representativePhone, isEditingRepresentativePhone])

	const handleEditFacilityName = () => {
		setIsEditingFacilityName(true)
		setFacilityName(dialerDetails?.placowkaName || '')
	}

	const handleCancelFacilityName = () => {
		setIsEditingFacilityName(false)
		setFacilityName(dialerDetails?.placowkaName || '')
	}

	const handleSaveFacilityName = () => {
		setIsEditingFacilityName(false)
		onFacilityNameUpdate(facilityName)
	}

	const handleEditAddress = () => {
		setIsEditingAddress(true)
		setFacilityAddress(dialerDetails?.placowkaAddress || '')
	}

	const handleCancelAddress = () => {
		setIsEditingAddress(false)
		setFacilityAddress(dialerDetails?.placowkaAddress || '')
	}

	const handleSaveAddress = () => {
		setIsEditingAddress(false)
		onFacilityAddressUpdate(facilityAddress)
	}

	const handleEditRepresentativeName = () => {
		setIsEditingRepresentativeName(true)
		setRepresentativeName(dialerDetails?.representativeName || '')
	}

	const handleCancelRepresentativeName = () => {
		setIsEditingRepresentativeName(false)
		setRepresentativeName(dialerDetails?.representativeName || '')
	}

	const handleSaveRepresentativeName = () => {
		setIsEditingRepresentativeName(false)
		onRepresentativeNameUpdate(representativeName)
	}

	const handleEditRepresentativePhone = () => {
		setIsEditingRepresentativePhone(true)
		setRepresentativePhone(dialerDetails?.representativePhone || '')
	}

	const handleCancelRepresentativePhone = () => {
		setIsEditingRepresentativePhone(false)
		setRepresentativePhone(dialerDetails?.representativePhone || '')
	}

	const handleSaveRepresentativePhone = () => {
		setIsEditingRepresentativePhone(false)
		onRepresentativePhoneUpdate(representativePhone)
	}

	const placowkaName = dialerDetails?.placowkaName || t('dialer.facilityCard.defaultName')

	return (
		<div className="rounded-md border border-slate-200 bg-white shadow-sm">
			<div className="flex w-full items-center justify-between space-x-6 p-6 py-4">
				<div className="flex-1 truncate">
					<div className="flex items-center space-x-3">
						<h2 className="truncate text-lg text-gray-900 font-semibold">{placowkaName}</h2>
					</div>
				</div>
				<div className="flex items-center justify-end">
					<div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-slate-100 outline -outline-offset-1 outline-black/5">
						<BuildingOffice2Icon className="size-7 text-slate-500" />
					</div>
				</div>
			</div>

			<div className="px-6 pb-6">
				<div className="py-2 flex items-start gap-3">
					<BuildingOffice2Icon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-40 sm:flex-none">{t('dialer.facilityCard.facilityName')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingFacilityName ? (
								<div className="flex items-center gap-2">
									<input
										type="text"
										value={facilityName}
										onChange={(e) => setFacilityName(e.target.value)}
										placeholder={t('dialer.facilityCard.facilityNamePlaceholder')}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSaveFacilityName}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelFacilityName}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{placowkaName || '—'}</span>
									<button
										type="button"
										onClick={handleEditFacilityName}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('dialer.facilityCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>

				<div className="py-2 flex items-start gap-3">
					<MapPinIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-40 sm:flex-none">{t('dialer.facilityCard.address')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingAddress ? (
								<div className="flex items-center gap-2">
									<input
										type="text"
										value={facilityAddress}
										onChange={(e) => setFacilityAddress(e.target.value)}
										placeholder={t('dialer.facilityCard.addressPlaceholder')}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSaveAddress}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelAddress}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{facilityAddress || '—'}</span>
									<button
										type="button"
										onClick={handleEditAddress}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('dialer.facilityCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>

				<div className="py-2 flex items-start gap-3">
					<UserCircleIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-40 sm:flex-none">{t('dialer.facilityCard.representativeName')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingRepresentativeName ? (
								<div className="flex items-center gap-2">
									<input
										type="text"
										value={representativeName}
										onChange={(e) => setRepresentativeName(e.target.value)}
										placeholder={t('dialer.facilityCard.representativeNamePlaceholder')}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSaveRepresentativeName}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelRepresentativeName}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{representativeName || '—'}</span>
									<button
										type="button"
										onClick={handleEditRepresentativeName}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('dialer.facilityCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>

				<div className="py-2 flex items-start gap-3">
					<DevicePhoneMobileIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-40 sm:flex-none">{t('dialer.facilityCard.representativePhone')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingRepresentativePhone ? (
								<div className="flex items-center gap-2">
									<input
										type="tel"
										value={representativePhone}
										onChange={(e) => setRepresentativePhone(e.target.value)}
										placeholder={t('dialer.facilityCard.representativePhonePlaceholder')}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSaveRepresentativePhone}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelRepresentativePhone}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{representativePhone || '—'}</span>
									<button
										type="button"
										onClick={handleEditRepresentativePhone}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('dialer.facilityCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>
			</div>
		</div>
	)
}
